import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';

import { logger } from '@azure/storage-blob';
import { Box, Button, Grid, IconButton, styled, useTheme } from '@mui/material';
import { useChannel } from 'ably/react';
import { ChevronLeft } from 'lucide-react';

import { CfsApi } from '@/api';
import { IcArrowLeft, IcArrowRight } from '@/assets/images';
import { Page, UnitShiftList } from '@/components';
import { DragAndDropProvider } from '@/components/DragAndDrop/DragAndDropProvider';
import { ExpandControl, FireHydrantMap } from '@/components/GoogleMap';
import { ResetMapControl } from '@/components/GoogleMap/ResetMapControl';
import { SearchFilterControl } from '@/components/GoogleMap/SearchFilterControl';
import { defaultMapCenter } from '@/config';
import { LocalStorageItems } from '@/constants';
import { CFSProvider, UnitShiftProvider } from '@/contexts';
import { useCFSContext, useUnitShiftContext } from '@/hooks';
import { IUnit } from '@/models';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { isOpenedInNewTab } from '@/utils';

interface UnitFormProps {
  unit?: IUnit;
}

export const NEW_CFS_MARKER_ID = 'new-cfs-id';

const HideButton = styled(Button)`
  writing-mode: tb-rl;
  transform: rotate(180deg);
  height: 100%;
  font-size: 15px;
  min-width: 20px;
  background-color: ${({ theme }) => theme.palette.background.default};
  color: ${({ theme }) => theme.palette.text.primary};
  align-items: baseline;
  padding: 12px;
  border-radius: 0 8px 8px 0;
  &:hover {
    background-color: ${({ theme }) => theme.palette.background.paper};
  }
`;

const UnitShifts: FC<UnitFormProps> = () => {
  const loadedUnitShiftModalOpen =
    JSON.parse(
      localStorage.getItem(LocalStorageItems.UnitShiftListOpen) ?? 'true',
    ) === true;

  const [isUnitShiftModalOpen, setIsUnitShiftModalOpen] = useState(
    loadedUnitShiftModalOpen,
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { account } = useUserStore();
  const { user } = useUserStore();
  const {
    fetchAssignedUnitShifts,
    activeCfsMarkers,
    filteredUnitShiftMarkers,
    filteredCfsMarkers,
  } = useUnitShiftContext();

  const { cfsMapMarkers = [] } = useCFSContext();
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>();
  const [resetTrigger, setResetTrigger] = useState(0);

  const cfsId = searchParams.get('cfsId') || undefined;
  const isOpenedInNewTabMode = isOpenedInNewTab(searchParams);
  const backRouteParam = searchParams.get('backRoute');
  const backRoute = useMemo(() => {
    if (!backRouteParam) return undefined;
    try {
      return decodeURIComponent(backRouteParam);
    } catch {
      return backRouteParam;
    }
  }, [backRouteParam]);
  const cfsMarkers =
    activeCfsMarkers.filter((marker) => !cfsId || marker.key === cfsId) || [];
  const accountCoordinates = account?.settings.center?.coordinates;
  const cfsPosition = cfsMarkers[0]?.position;

  useEffect(() => {
    if (cfsPosition && cfsId) {
      setMapCenter(cfsPosition);
    } else {
      if (accountCoordinates) {
        setMapCenter({
          lng: accountCoordinates[0],
          lat: accountCoordinates[1],
        });
      } else {
        setMapCenter(defaultMapCenter);
      }
    }
  }, [cfsId, JSON.stringify(cfsPosition), filteredCfsMarkers]);

  const handleExpandMap = async () => {
    window.close();
  };

  useEffect(() => {
    fetchAssignedUnitShifts();
  }, []);

  // Listen for CFS status changes
  useChannel(`account:${account?._id}:cfs:all`, async () => {
    fetchAssignedUnitShifts();
  });

  const handleMapTypeIdChange = () => {
    if (!searchParams.has('mapTypeId')) return;

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('mapTypeId');
    setSearchParams(newSearchParams);
  };

  const handleResetMap = useCallback(() => {
    if (mapCenter) {
      setResetTrigger((prev) => prev + 1);
    }
  }, [mapCenter]);

  const handleHideToggle = () => {
    setIsUnitShiftModalOpen((v) => {
      localStorage.setItem(LocalStorageItems.UnitShiftListOpen, String(!v));
      return !v;
    });
  };

  const handleBack = () => {
    if (backRoute) {
      navigate(backRoute);
      return;
    }
    navigate(-1);
  };

  const enhancedMarkers = useMemo(() => {
    return [
      ...filteredCfsMarkers
        .filter((cfsMarker) => {
          if (!cfsId) return true;
          return cfsMarker.key === cfsId;
        })
        .map((marker) => ({ ...marker, isDroppable: true })),
      ...(filteredUnitShiftMarkers || []).map((marker) => ({
        ...marker,
        isDroppable: false,
      })),
      ...cfsMapMarkers.map((marker) => ({ ...marker, isDroppable: false })),
    ].map((marker) => ({
      ...marker,
      cfsId: marker.cfsId || marker.key || '[default-id]',
      cfsNumber: marker.cfsNumber || marker.title || '[default-number]',
      address: marker.address ? String(marker.address) : '[default-address]',
    }));
  }, [filteredCfsMarkers, filteredUnitShiftMarkers, cfsMapMarkers]);

  const isAccountWideView = location.pathname === '/unit-shifts';

  const isDispatcher = user?.roles?.some((r) => r.role === 'DISPATCHER');

  return (
    <Page title="Unit and unit shifts management" style={{ paddingTop: 0 }}>
      {backRoute && !isOpenedInNewTabMode && (
        <IconButton
          aria-label="Go back"
          onClick={handleBack}
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 1400,
            backgroundColor: 'common.white',
            border: '1px solid',
            borderColor: 'grey.400',
            color: 'error.main',
            borderRadius: '6px',
            padding: '4px',
            '&:hover': {
              backgroundColor: 'common.white',
            },
          }}
        >
          <ChevronLeft />
        </IconButton>
      )}
      <Grid container>
        <Grid
          xs={isUnitShiftModalOpen ? 8 : 11.5}
          md={isUnitShiftModalOpen ? 8 : 11.7}
          lg={isUnitShiftModalOpen ? 8 : 11.75}
          item
        >
          <FireHydrantMap
            key={resetTrigger}
            center={mapCenter}
            defaultZoom={12}
            markers={enhancedMarkers}
            onHandleMapTypeIdChange={handleMapTypeIdChange}
            showContextMenu={true}
            showStreetViewControl={false}
            clickableIcons={false}
            style={{ height: '100vh' }}
          >
            {!!cfsId && (
              <ExpandControl onCondense={handleExpandMap} isExpanded />
            )}
            <ResetMapControl onReset={handleResetMap} />
            <SearchFilterControl />
          </FireHydrantMap>
        </Grid>
        <Grid
          xs={isUnitShiftModalOpen ? 4 : 0.5}
          md={isUnitShiftModalOpen ? 4 : 0.3}
          lg={isUnitShiftModalOpen ? 4 : 0.25}
          height="100vh"
          item
        >
          <Box display="flex" flexDirection="row" height="100vh">
            <HideButton
              sx={{ minWidth: isUnitShiftModalOpen ? '' : '100%' }}
              variant="contained"
              onClick={handleHideToggle}
            >
              {isUnitShiftModalOpen ? (
                <IcArrowLeft style={{ marginTop: 10 }} />
              ) : (
                <IcArrowRight style={{ marginTop: 10 }} />
              )}
            </HideButton>
            {isUnitShiftModalOpen && (
              <Box display="flex" flex={1} overflow="hidden">
                <UnitShiftList
                  cfsId={cfsId}
                  isUnitShiftsPage
                  isAccountWideView={isAccountWideView}
                  isDispatcher={!!isDispatcher}
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Page>
  );
};

const UnitShiftScreen = () => {
  const [queryParams] = useSearchParams();
  const cfsId = queryParams.get('cfsId') || undefined;
  const { updateToast } = useToastStore();
  const [showLoader, setShowLoader] = useState(false);

  const unitShiftContext = useUnitShiftContext();

  const handleDragDropAssignUnit = async (
    unitIdOrShiftId: string,
    targetCfsId: string,
  ) => {
    try {
      setShowLoader(true);
      try {
        await CfsApi.assignUnitShift(targetCfsId, unitIdOrShiftId);

        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: `Unit was successfully assigned to CFS.`,
        });

        await unitShiftContext.fetchAssignedUnitShifts();
        return;
      } catch (directAssignError) {
        logger.error(
          'Direct assignment failed, will try to find unit shift:',
          directAssignError,
        );
      }

      const unitId = unitIdOrShiftId;
      let unitShiftId = null;

      const onDutyShift = unitShiftContext.onDutyUnitShifts?.find(
        (shift) => shift.unit._id === unitId,
      );
      if (onDutyShift) {
        unitShiftId = onDutyShift._id;
      }

      if (unitShiftId) {
        await CfsApi.assignUnitShift(targetCfsId, unitShiftId);

        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: `Unit was successfully assigned to CFS.`,
        });
      } else {
        logger.error('No unit shift found for unit:', unitId);
        throw new Error(
          'Could not assign unit. No active shift found for this unit.',
        );
      }
    } catch (err: any) {
      updateToast({
        open: true,
        message: err.message || 'Failed to assign unit to CFS.',
        type: ConfirmTypes.WARNING,
      });
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <CFSProvider cfsId={cfsId}>
      <UnitShiftProvider>
        <DragAndDropProvider onAssignUnit={handleDragDropAssignUnit}>
          <UnitShifts />
          {showLoader && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
              }}
            >
              <div
                style={{
                  padding: 20,
                  borderRadius: 8,
                  backgroundColor: useTheme().palette.background.paper,
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                }}
              >
                Assigning unit to CFS...
              </div>
            </div>
          )}
        </DragAndDropProvider>
      </UnitShiftProvider>
    </CFSProvider>
  );
};

export default UnitShiftScreen;
